import type { PreferredHost } from "@spectree/shared";
import { api } from "./client";

/**
 * Authenticated user profile returned by GET /api/v1/me.
 *
 * This intentionally does NOT extend the shared `User` type — the `/me`
 * endpoint returns only the safe-to-display profile fields plus a derived
 * `hasFogbugzKey` flag and a derived `hasAdoPat` flag (neither the encrypted
 * FogBugz key nor the encrypted Azure DevOps PAT is ever sent to the browser). It also omits team-membership fields like `role`/`teamId`
 * which are surfaced via separate endpoints.
 */
export interface Me {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  timeZone: string | null;
  /** Preferred CLI host; null when the user has not chosen one (A14). */
  preferredHost: PreferredHost | null;
  isActive: boolean;
  isGlobalAdmin: boolean;
  hasFogbugzKey: boolean;
  /** Whether an Azure DevOps PAT is saved. The token itself is never returned. */
  hasAdoPat: boolean;
  /**
   * Whether Grooming and SpecTree Toolbox pages are shown: the user is a
   * global admin or holds a Toolbox role on at least one project (E283).
   * What each page offers comes from `GET /toolbox/access` (see
   * `useCan` in `@/hooks/queries/use-toolbox-access`).
   */
  groomingVisible: boolean;
  createdAt: string;
  updatedAt: string;
}

interface MeResponse {
  data: Me;
}

export interface StoreFogbugzKeyInput {
  apiKey: string;
}

export interface SetAdoPatInput {
  /** Organisation-scoped Azure DevOps PAT (scope: Work Items Read & Write). */
  pat: string;
}

/** How-to values for making an ADO PAT in this SpecTree's organisation (GET /me/ado-pat). */
export interface AdoPatHowTo {
  /** The token name to type, "SpecTree Toolbox". */
  tokenName: string;
  /** The ADO organisation name (e.g. `rivcode`). */
  organization: string;
  /** How long the token should last, in days. */
  expiryDays: number;
  /** The one scope to tick, "Work Items: Read & write". */
  scope: string;
  /**
   * The extra scope Admins tick, "Code: Read": the Repos page's repository and
   * branch pickers read ADO Git. Shown only to people who hold `manage-repos`.
   */
  adminScope: string;
  /** ADO's personal access tokens page for the organisation. */
  tokensUrl: string;
}

/**
 * `data` of GET /api/v1/me/ado-pat (epic PI-SPEC-SW-E288): whether a PAT is
 * saved, when, and the how-to values. Never the token itself.
 */
export interface AdoPatStatus {
  saved: boolean;
  /**
   * ISO time the PAT was saved after passing the connect test; null when none is
   * saved, it was saved untested (Settings) or before this was recorded.
   */
  savedAt: string | null;
  organization: string;
  howTo: AdoPatHowTo;
}

/** Why ADO refused a tested PAT (POST /me/ado-pat/connect). */
export type AdoPatTestFailureReason = "invalid" | "wrong-organization" | "no-work-items-scope" | "ado-unreachable";

/**
 * `data` of POST /api/v1/me/ado-pat/connect: the token passed ADO's test and
 * was saved, or it was refused with ADO's reason in plain words and nothing
 * was stored.
 */
export type ConnectAdoPatResult =
  | {
      ok: true;
      organization: string;
      readableProjects: string[];
      savedAt: string;
      /** Only for people who set up repositories: whether the token reads code (Code: Read). A warning, never a refusal. */
      codeRead?: boolean;
    }
  | { ok: false; reason: AdoPatTestFailureReason; message: string };

export interface SetPreferredHostInput {
  host: PreferredHost;
}

export const meApi = {
  /**
   * Get current user information including FogBugz key status
   */
  me: () => api.get<MeResponse>("/me"),

  /**
   * Store FogBugz API key (encrypted)
   */
  storeFogbugzKey: (data: StoreFogbugzKeyInput) =>
    api.put<{ message: string }>("/me/fogbugz-key", data),

  /**
   * Clear FogBugz API key
   */
  clearFogbugzKey: () => api.delete<{ message: string }>("/me/fogbugz-key"),

  /**
   * Store the Azure DevOps PAT (encrypted server-side; never returned)
   */
  setAdoPat: (data: SetAdoPatInput) => api.put<{ message: string }>("/me/ado-pat", data),

  /**
   * Azure DevOps PAT status and how-to values (never the token)
   */
  getAdoPatStatus: () => api.get<{ data: AdoPatStatus }>("/me/ado-pat"),

  /**
   * Test an Azure DevOps PAT against ADO and save it only when it works.
   * A refusal is an answer (`ok: false`), not an error.
   */
  connectAdoPat: (data: SetAdoPatInput) => api.post<{ data: ConnectAdoPatResult }>("/me/ado-pat/connect", data),

  /**
   * Clear the Azure DevOps PAT
   */
  clearAdoPat: () => api.delete<{ message: string }>("/me/ado-pat"),

  /**
   * Set the preferred CLI host ('copilot' | 'claude')
   */
  setPreferredHost: (data: SetPreferredHostInput) =>
    api.put<{ message: string }>("/me/preferred-host", data),
};
